/**
 * State machine + attempt-counter policy for the web screen lock.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/AppLockState.swift` exactly, with
 * these web adaptations:
 *
 *   - "PIN" becomes "passphrase" in the event names to match the web spec.
 *   - Times are `number` (Unix milliseconds) instead of Swift `Date`.
 *   - The static `backgroundLockMs` mirrors iOS's `backgroundLockSeconds`
 *     but carries milliseconds for JS consistency. The `configBackgroundLockMs`
 *     event updates it.
 *   - Biometric = `navigator.credentials.get({publicKey:...})` WebAuthn
 *     policy hook.  No actual `.get()` call lives here — this file is
 *     pure-logic + state-transition only.
 *
 * Threat model:
 *
 *   - The wipePending terminal state is the nuclear option: 12 wrong
 *     passphrases and local data is destroyed. The counter persists in
 *     IndexedDB across cold launches so a hard page reload doesn't reset it.
 *   - Biometric is a CONVENIENCE, not a credential:
 *       1. Three biometric failures → .lockedRequirePassphraseAfterBiometricFailure
 *          from which biometric successes are *ignored*.
 *       2. After 24h since last passphrase entry, biometric is refused
 *          regardless of failure count.
 */

// ---------------------------------------------------------------------------
// State alphabet
// ---------------------------------------------------------------------------

export type AppLockState =
  | "unlocked"
  | "lockedRequirePassphrase"
  | "lockedRequirePassphraseAfterBiometricFailure"
  | "wipePending";

// ---------------------------------------------------------------------------
// Supporting value types
// ---------------------------------------------------------------------------

export interface AppLockAttemptHistory {
  /** Consecutive wrong-passphrase attempts since last correct entry. */
  readonly attempts: number;
  /** When the last failure happened (ms since epoch). `null` if none. */
  readonly lastFailedAt: number | null;
  /** Wall-clock instant (ms) before which the next attempt is gated. */
  readonly lockoutUntil: number | null;
}

export const ZERO_HISTORY: AppLockAttemptHistory = {
  attempts: 0,
  lastFailedAt: null,
  lockoutUntil: null,
};

export interface AppLockStatus {
  readonly state: AppLockState;
  readonly history: AppLockAttemptHistory;
  /**
   * Last time the user successfully entered the passphrase (NOT biometric).
   * 24h after this, biometric alone is no longer sufficient.
   * Milliseconds since epoch.
   */
  readonly lastUnlockedAt: number;
}

/** Returns a freshly-locked status suitable for cold-launch or first-install. */
export function initialStatus(nowMs: number = Date.now()): AppLockStatus {
  return {
    state: "lockedRequirePassphrase",
    history: ZERO_HISTORY,
    lastUnlockedAt: nowMs,
  };
}

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

export type AppLockEvent =
  | { type: "appBackgrounded" }
  | { type: "appForegroundedAfterMs"; elapsedMs: number }
  | { type: "userEnteredCorrectPassphrase" }
  | { type: "userEnteredWrongPassphrase" }
  | { type: "biometricSucceeded" }
  | { type: "biometricFailedThresholdReached" }
  | { type: "configBackgroundLockMs"; ms: number }
  | { type: "sessionEnded" };

// ---------------------------------------------------------------------------
// Lockout policy
// ---------------------------------------------------------------------------

/**
 * Lockout policy table. Pure-function lookup — no I/O.
 *
 *   attempts | lockout
 *   ---------+----------------------------
 *   0..4     | none
 *   5..7     | 30s
 *   8..9     | 5m (300s)
 *   10..11   | 30m (1800s)
 *   12+      | wipe pending (caller transitions state)
 *
 * Mirrors `AppLockLockoutPolicy` in iOS `AppLockState.swift` exactly.
 */
export const LOCKOUT_POLICY = {
  attempt5LockoutMs:  30_000,   // 30 seconds
  attempt8LockoutMs:  300_000,  // 5 minutes
  attempt10LockoutMs: 1_800_000, // 30 minutes
  wipeThreshold:      12,
} as const;

/**
 * Returns the lockout duration in ms given a post-increment attempt count,
 * or `null` if no lockout applies.
 *
 * At/above the wipe threshold the caller is expected to transition to
 * `wipePending`; the lockout duration is meaningless at that point.
 */
export function nextLockoutMs(attempts: number): number | null {
  if (attempts < 5)   return null;
  if (attempts <= 7)  return LOCKOUT_POLICY.attempt5LockoutMs;
  if (attempts <= 9)  return LOCKOUT_POLICY.attempt8LockoutMs;
  if (attempts <= 11) return LOCKOUT_POLICY.attempt10LockoutMs;
  // attempts >= 12 → wipe pending
  return null;
}

// ---------------------------------------------------------------------------
// State machine
// ---------------------------------------------------------------------------

/**
 * Background-lock threshold in milliseconds. Updated by the
 * `configBackgroundLockMs` event. Defaults to 60_000 (1 minute).
 *
 * Negative value = "never auto-lock on background".
 *
 * Stored as a module-level variable (not in AppLockStatus) so it is a user
 * preference separate from the per-attempt persisted state, mirroring iOS's
 * `AppLockStateMachine.backgroundLockSeconds` static var.
 */
let backgroundLockMs = 60_000;

/** 24 hours in milliseconds. After this, biometric alone is insufficient. */
export const BIOMETRIC_24H_WINDOW_MS = 24 * 60 * 60 * 1_000;

/**
 * Get the current in-memory background-lock threshold (for tests / introspection).
 */
export function getBackgroundLockMs(): number {
  return backgroundLockMs;
}

/**
 * Set the background-lock threshold directly (used by AppLockStore after
 * loading config from IndexedDB, and by tests).
 */
export function setBackgroundLockMs(ms: number): void {
  backgroundLockMs = ms;
}

/**
 * Pure transition function. Takes the current status + an event + the current
 * wall-clock time (ms), returns the next status.
 *
 * Persisting the result to IndexedDB is the caller's responsibility
 * (see AppLockStore.ts).
 *
 * `wipePending` is terminal: every subsequent event (including
 * `userEnteredCorrectPassphrase`) keeps us there until the wipe completes.
 */
export function transition(
  status: AppLockStatus,
  event: AppLockEvent,
  nowMs: number = Date.now(),
): AppLockStatus {
  // Terminal: once wipe is queued, no event can escape it.
  if (status.state === "wipePending") {
    return status;
  }

  switch (event.type) {
    case "sessionEnded":
      // Explicit session end: always lock. Equivalent to a cold launch.
      return { ...status, state: "lockedRequirePassphrase" };

    case "appBackgrounded":
      // Backgrounding alone doesn't lock — we measure elapsed time on
      // foreground return. Matches Signal: a quick pull-down-the-shade
      // shouldn't re-prompt for the passphrase.
      return status;

    case "appForegroundedAfterMs": {
      if (backgroundLockMs < 0) return status;
      if (event.elapsedMs < backgroundLockMs) return status;
      if (status.state !== "unlocked") return status;
      return {
        ...status,
        state: "lockedRequirePassphrase",
      };
    }

    case "userEnteredCorrectPassphrase":
      // Always wins (except wipePending, handled above). Reset counters and
      // refresh lastUnlockedAt so the 24h biometric window restarts.
      return {
        state: "unlocked",
        history: ZERO_HISTORY,
        lastUnlockedAt: nowMs,
      };

    case "userEnteredWrongPassphrase": {
      const nextAttempts = status.history.attempts + 1;

      // 12 strikes → wipe.
      if (nextAttempts >= LOCKOUT_POLICY.wipeThreshold) {
        return {
          state: "wipePending",
          history: {
            attempts: nextAttempts,
            lastFailedAt: nowMs,
            lockoutUntil: null,
          },
          lastUnlockedAt: status.lastUnlockedAt,
        };
      }

      const lockoutDurationMs = nextLockoutMs(nextAttempts);
      // If we crossed into a higher bucket, set a fresh lockoutUntil;
      // otherwise keep the existing one.
      const lockoutUntil =
        lockoutDurationMs !== null
          ? nowMs + lockoutDurationMs
          : status.history.lockoutUntil;

      return {
        state: status.state, // still locked, just deeper
        history: {
          attempts: nextAttempts,
          lastFailedAt: nowMs,
          lockoutUntil,
        },
        lastUnlockedAt: status.lastUnlockedAt,
      };
    }

    case "biometricSucceeded": {
      switch (status.state) {
        case "unlocked":
          return status; // already unlocked

        case "lockedRequirePassphraseAfterBiometricFailure":
          // Biometric driver should not have called us here, but defensively
          // keep the passphrase requirement.
          return status;

        case "lockedRequirePassphrase": {
          const elapsed = nowMs - status.lastUnlockedAt;
          if (elapsed > BIOMETRIC_24H_WINDOW_MS) {
            // 24h since last passphrase — passphrase required.
            return status;
          }
          return {
            state: "unlocked",
            // Deliberately do NOT refresh lastUnlockedAt — the 24h window is
            // anchored to the last passphrase entry, not any biometric unlock.
            history: ZERO_HISTORY,
            lastUnlockedAt: status.lastUnlockedAt,
          };
        }

        case "wipePending":
          return status; // unreachable (handled at top), kept for exhaustiveness
      }
      break;
    }

    case "biometricFailedThresholdReached": {
      switch (status.state) {
        case "lockedRequirePassphrase":
        case "lockedRequirePassphraseAfterBiometricFailure":
          return {
            ...status,
            state: "lockedRequirePassphraseAfterBiometricFailure",
          };

        case "unlocked":
        case "wipePending":
          return status;
      }
      break;
    }

    case "configBackgroundLockMs":
      // Update the module-level threshold. AppLockStore is responsible for
      // persisting the value; we just mirror it into in-memory state so
      // subsequent appForegroundedAfterMs events use the new value immediately.
      backgroundLockMs = event.ms;
      return status;
  }

  // TypeScript exhaustiveness guard
  return status;
}
